import { useState, useEffect, useRef, useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import { createVisitor } from '../../services/VisitorSevice';
import Card from '../../components/Card/Card';
import Loader from '../../components/Loader/Loader';
import VisitorForm from './VisitorForm';
import { useNavigate, useParams } from 'react-router-dom';

const VisitorManage = () => {
  const [visitor, setVisitor] = useState({
    name: '',
    phone: '',
    purpose: '',
    meeting_with: '',
    category_id: ''
  });
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [stream, setStream] = useState(null);
  const [cameraState, setCameraState] = useState({
    permission: 'prompt',
    error: null,
    ready: false,
    supported: true
  });

  const updateCameraState = useCallback((updates) => {
    setCameraState(prev => ({ ...prev, ...updates }));
  }, []);

  const handleCameraError = useCallback((error) => {
    console.error('Camera error:', error);

    let errorMessage = 'Camera error occurred';
    let permissionState = 'denied';
    let isSupported = true;

    switch (error.name) {
      case 'NotAllowedError':
        errorMessage = 'Camera access denied. Please allow permissions.';
        break;
      case 'NotFoundError':
        errorMessage = 'No camera device found.';
        permissionState = 'prompt';
        break;
      case 'NotReadableError':
        errorMessage = 'Camera is already in use.';
        break;
      case 'SecurityError':
        errorMessage = 'Camera access requires HTTPS or localhost.';
        isSupported = false;
        break;
      case 'NotSupportedError':
        errorMessage = 'Camera API not supported in this browser.';
        isSupported = false;
        break;
      default:
        errorMessage = error.message;
    }

    updateCameraState({
      permission: permissionState,
      error: errorMessage,
      ready: false,
      supported: isSupported
    });

    enqueueSnackbar(errorMessage, {
      variant: 'error',
      autoHideDuration: 10000,
      persist: !isSupported
    });
  }, [updateCameraState]);

  const checkPermissions = useCallback(async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' });
        updateCameraState({ permission: permissionStatus.state });

        permissionStatus.onchange = () => {
          updateCameraState({ permission: permissionStatus.state });
        };
      }
    } catch (error) {
    }
  }, [updateCameraState]);

  const startCamera = useCallback(async () => {
    try {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 320 },
          height: { ideal: 240 },
          facingMode: 'user'
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
        updateCameraState({
          permission: 'granted',
          ready: true,
          error: null,
          supported: true
        });
      }
    } catch (error) {
      handleCameraError(error);
    }
  }, [handleCameraError, stream, updateCameraState]);

  useEffect(() => {
    // Check if we're in a secure context (HTTPS or localhost)
    const isSecureContext = window.isSecureContext ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '43.204.137.210:8071';

    if (!isSecureContext) {
      handleCameraError({
        name: 'SecurityError',
        message: 'Camera access requires HTTPS or localhost'
      });
      return;
    }

    // Check if media devices are supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      handleCameraError({
        name: 'NotSupportedError',
        message: 'Camera API not supported in this browser'
      });
      return;
    }

    const initializeCamera = async () => {
      try {
        await checkPermissions();
        await startCamera();
      } catch (error) {
        handleCameraError(error);
      }
    };

    initializeCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [checkPermissions, handleCameraError, startCamera, stream]);

  const captureImage = () => {
    if (!cameraState.ready || !videoRef.current || !canvasRef.current) {
      enqueueSnackbar('Camera not ready', { variant: 'error' });
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    try {
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(blob => {
        if (!blob) {
          throw new Error('Failed to create image blob');
        }

        const file = new File([blob], 'visitor-photo.jpg', { type: 'image/jpeg' });
        setImage(file);
        setPreviewUrl(canvas.toDataURL('image/jpeg'));

        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setStream(null);
        }

        updateCameraState({ ready: false });
        enqueueSnackbar('Photo captured successfully', { variant: 'success' });
      }, 'image/jpeg', 0.92);
    } catch (error) {
      console.error('Capture error:', error);
      enqueueSnackbar('Failed to capture image', { variant: 'error' });
    }
  };

  const retakePhoto = () => {
    setPreviewUrl(null);
    setImage(null);
    startCamera();
  };

  const showPermissionHelp = () => {
    enqueueSnackbar(
      <div>
        <p>To enable camera:</p>
        <ol>
          <li>Check browser address bar for camera icon</li>
          <li>Click and select "Allow"</li>
          <li>Refresh page or click Retry</li>
        </ol>
      </div>,
      {
        variant: 'info',
        persist: true
      }
    );
  };

  const handleChange = (e) => {
    setVisitor({ ...visitor, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!image) {
      enqueueSnackbar('Please capture visitor photo first', { variant: 'error' });
      return;
    }

    setLoader(true);

    try {
      const formData = new FormData();
      formData.append('name', visitor.name);
      formData.append('phone', visitor.phone);
      formData.append('purpose', visitor.purpose);
      formData.append('meeting_with', visitor.meeting_with);
      formData.append('category_id', visitor.category_id);
      formData.append('image', image);

      const response = await createVisitor(formData);
      if (response.status === 200) {
        enqueueSnackbar('Visitor created successfully', { variant: 'success' });
        navigate('/visitors');
      } else {
        throw new Error('Unexpected server response');
      }
    } catch (error) {
      console.error('Error creating visitor:', error);
      enqueueSnackbar('Failed to create visitor', { variant: 'error' });
    } finally {
      setLoader(false);
    }
  };

  const back = () => {
    navigate('/visitors');
  };

  return (
    <Card componentName={id ? 'Update Visitor' : 'Add Visitor'} back={back}>
      {loader ? (
        <Loader />
      ) : (
        <VisitorForm
          name={visitor.name}
          phone={visitor.phone}
          category_id={visitor.category_id}
          purpose={visitor.purpose}
          meeting_with={visitor.meeting_with}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          captureImage={captureImage}
          retakePhoto={retakePhoto}
          videoRef={videoRef}
          canvasRef={canvasRef}
          image={image}
          previewUrl={previewUrl}
          cameraState={cameraState}
          startCamera={startCamera}
          showPermissionHelp={showPermissionHelp}
        />
      )}
    </Card>
  );
};

export default VisitorManage;
